<script lang="ts">
import {
  defineComponent,
  ref,
  watch,
  onMounted,
  nextTick,
} from 'vue'
import EyeIcon from 'vue-material-design-icons/Eye.vue'
import EyeOffIcon from 'vue-material-design-icons/EyeOff.vue'
import { inputTextDefaultProps } from '@/models/inputTextPartial'

export default defineComponent({
  name: 'InputPasswordDefault',

  components: {
    EyeIcon,
    EyeOffIcon,
  },

  props: {
    ...inputTextDefaultProps,
  },

  emits: ['update:modelValue', 'trim', 'focus', 'blur'],

  setup(props, { emit }) {
    const localValue = ref('')
    const focus = ref(false)
    const isPasswordVisible = ref(false)

    watch(() => props.modelValue, (newValue) => {
      if (localValue.value !== newValue) localValue.value = newValue
    })

    onMounted(() => {
      localValue.value = props.modelValue
    })

    const onFocus = () => {
      focus.value = true
      emit('focus')
    }

    const onBlur = async () => {
      focus.value = false

      if (props.hasTrim) {
        const _value = localValue.value
        localValue.value = localValue.value.trim() // 前後の空白を削除

        if (_value !== localValue.value) {
          emit('trim', localValue.value)
        }
      }

      await nextTick()
      emit('blur')
    }

    /**
     * パスワード文字列の表示 / 非表示切り替え
     */
    const togglePasswordVisibility = () => {
      isPasswordVisible.value = !isPasswordVisible.value
    }

    return {
      // data
      localValue,
      focus,
      isPasswordVisible,

      // method
      onFocus,
      onBlur,
      togglePasswordVisibility,
    }
  }
})
</script>

<template>
  <div
    class="l-unit__inputPassword"
    :data-is-error="isError"
    :data-focus="focus"
    :data-disabled="isDisabled">
    <div class="l-unit__inputPassword--description">
      <div class="o-input__password">
        <input
          v-model="localValue"
          class="o-input__password--item"
          :type="isPasswordVisible ? 'text' : 'password'"
          :name="name ? name : undefined"
          :placeholder="placeholder"
          :disabled="isDisabled"
          :readonly="readonly"
          :required="required"
          :autocomplete="autocomplete"
          :pattern="pattern"
          :inputmode="inputmode"
          :maxlength="maxlength"
          :data-is-error="isError"
          @input="$emit('update:modelValue', localValue)"
          @focus="onFocus"
          @blur="onBlur">
      <!-- /.o-input__password --></div>
    <!-- /.l-unit__inputPassword description --></div>

    <div class="l-unit__inputPassword--action">
      <div class="o-action__toggleVisibility">
        <!--
          button 要素は type="button" を指定しないとデフォルトで submit になるため、
          Enter キーの入力に反応してしまうため、type="button" を指定。
        -->
        <button
          class="o-action__toggleVisibility--item u-action__tooltip"
          type="button"
          :data-tooltip="isPasswordVisible ? '元に戻す' : '伏字を外す'"
          @click.prevent="togglePasswordVisibility">
          <EyeIcon
            v-show="isPasswordVisible"
            fill-color="#9f9f9f" />

          <EyeOffIcon
            v-show="!isPasswordVisible"
            fill-color="#9f9f9f" />
        </button>
      <!-- /.o-action__toggleVisibility --></div>
    <!-- /.l-unit__inputPassword action --></div>
  <!-- /.l-unit__inputPassword --></div>
</template>

<style lang="sass" scoped>
.l-unit__inputPassword
  $root: &
  $icon-width-px: 22
  display: flex
  align-items: center
  padding: 0.6em 1em
  background-color: rgba($bg-xx-light-cyan, 1)
  border: 1px solid $border-xx-light-cyan
  border-radius: $border-radius
  +transition

  & &--description
    flex-grow: 1

  & &--action
    width: changeSizePx2Em($icon-width-px)
    margin-left: 0.5em

  +mouseover
    &[data-disabled="false"]
      border-color: $border-light-cyan

  &[data-focus="true"]
    background-color: $bg-white
    border: 1px solid $border-light-cyan
    box-shadow: $box-shadow

  &[data-disabled="true"]
    opacity: 0.4

  &[data-is-error="true"]
    background-color: rgba($bg-error, 1)
    border-color: $border-error

.o-input__password
  & &--item
    width: 100%
    display: block
    color: rgba($font-black, 0.87)
    border: none

    &::placeholder
      color: rgba($font-black, 0.38)

.o-action__toggleVisibility
  & &--item
    width: 100%
    display: block

    *
      display: block
</style>
